/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { When } from "react-if";
import {
  Table,
  Switch,
  Text,
  theme,
  SVGIcon,
  Row,
  Checkbox,
} from "@nordcloud/gnui";
import { isNotEmpty } from "~/tools";
import { EnvironmentOption } from "../types";
import { StyledTd, StyledTr, SwitchWrap, TdWithMinWidth } from "./styles";
import { AppEnvRowProps, SELECTOR_TYPE } from "./types";
import { isAppExpanded } from "./utils";

function AppEnvRow({
  name,
  nid,
  environments,
  selectedEnvs,
  setSelectedEnvs,
  selectedApps,
  setSelectedApps,
  selectionType,
  disabled = false,
}: AppEnvRowProps) {
  const [isRowOpen, setIsRowOpen] = useState(
    isAppExpanded({ nid, selectedEnvs, selectedApps, environments })
  );

  const isAppSelected = selectedApps.some((app) => app.nid === nid);

  const switchEnv = (inputEnv: EnvironmentOption) => {
    const newSelectedEnvs = [...selectedEnvs];
    if (isAppSelected) {
      switchApp();
      newSelectedEnvs.push(
        ...environments
          .filter(
            (env) =>
              env?.nid !== inputEnv?.nid &&
              !newSelectedEnvs
                .map((newEnv) => newEnv.nid)
                .includes(env?.nid ?? "")
          )
          .map((environment) => {
            return {
              nid: environment?.nid ?? "",
              name: environment?.name ?? "",
            };
          })
      );
      setSelectedEnvs(newSelectedEnvs);
      return;
    }

    const selectedOption = selectedEnvs.find(
      (env) => env.nid === inputEnv?.nid
    );
    if (selectedOption) {
      const indexOfEnv = selectedEnvs.indexOf(selectedOption);
      newSelectedEnvs.splice(indexOfEnv, 1);
    } else if (selectedEnvsCount === environments.length - 1) {
      switchApp();
      return;
    } else {
      newSelectedEnvs.push({
        nid: inputEnv?.nid ?? "",
        name: inputEnv?.name ?? "",
      });
    }
    setSelectedEnvs(newSelectedEnvs);
  };

  const switchApp = () => {
    const newSelectedApps = [...selectedApps];
    const selectedOption = selectedApps.find((app) => app.nid === nid);
    if (selectedOption) {
      const indexOfApp = selectedApps.indexOf(selectedOption);
      newSelectedApps.splice(indexOfApp, 1);
    } else {
      newSelectedApps.push({ nid, name });
      const newSelectedEnvs = selectedEnvs.filter(
        (env) =>
          !environments.map((environment) => environment?.nid).includes(env.nid)
      );
      setSelectedEnvs(newSelectedEnvs);
    }
    setSelectedApps(newSelectedApps);
  };

  const isEnvironmentSelected = (environment: EnvironmentOption) => {
    return selectedEnvs.some((env) => env.nid === environment?.nid);
  };

  const selectedEnvsCount = environments?.filter((environment) =>
    isEnvironmentSelected(environment)
  )?.length;

  const hasEnvironments = isNotEmpty(environments ?? []);

  return (
    <Table.tbody>
      <Table.tr>
        <TdWithMinWidth
          data-testid="application-expand"
          onClick={() => setIsRowOpen((prevState) => !prevState)}
        >
          <When condition={hasEnvironments}>
            <div css={{ transform: "translateY(5px)" }}>
              <SVGIcon
                name="chevronDown"
                css={{
                  transition: "transform .1s linear",
                  transform: isRowOpen ? "rotate(0deg)" : "rotate(-90deg)",
                }}
              />
            </div>
          </When>
        </TdWithMinWidth>
        <When condition={selectionType === SELECTOR_TYPE.checkbox}>
          <TdWithMinWidth>
            <When condition={hasEnvironments}>
              <Checkbox
                withoutLabel
                data-testid="application-select"
                checked={isAppSelected}
                onChange={switchApp}
              />
            </When>
          </TdWithMinWidth>
        </When>
        <Table.td css={{ wordBreak: "break-all" }}>
          <Text weight="medium" tag="span">
            {name}
          </Text>
          <Row css={{ marginLeft: 0 }}>
            <Text tag="span" size="sm" color={theme.color.text.text02}>
              {environments?.length} environment
              {environments?.length === 1 ? "" : "s"}
            </Text>
          </Row>
        </Table.td>
        <StyledTd />
        <When
          condition={selectionType === SELECTOR_TYPE.switch && hasEnvironments}
        >
          <StyledTd>
            <SwitchWrap>
              <Switch
                data-testid="application-select"
                checked={isAppSelected}
                disabled={disabled}
                onChange={switchApp}
              />
            </SwitchWrap>
          </StyledTd>
        </When>
      </Table.tr>

      {environments.map((environment) => (
        <StyledTr key={environment?.nid} show={isRowOpen}>
          <td />
          <When condition={selectionType === SELECTOR_TYPE.checkbox}>
            <StyledTd>
              <When condition={hasEnvironments}>
                <Checkbox
                  data-testid="environment-select"
                  checked={isAppSelected || isEnvironmentSelected(environment)}
                  onChange={() => {
                    switchEnv(environment);
                  }}
                />
              </When>
            </StyledTd>
          </When>
          <Table.td css={{ wordBreak: "break-all" }}>
            {environment?.name}
          </Table.td>
          <Table.td />
          <When condition={selectionType === SELECTOR_TYPE.switch}>
            <StyledTd>
              <SwitchWrap>
                <Switch
                  data-testid="environment-select"
                  disabled={disabled}
                  checked={isAppSelected || isEnvironmentSelected(environment)}
                  onChange={() => {
                    switchEnv(environment);
                  }}
                />
              </SwitchWrap>
            </StyledTd>
          </When>
        </StyledTr>
      ))}
    </Table.tbody>
  );
}

export { AppEnvRow };
